import { type PulseError, removeEmptyParams } from '@prenuvo/pulse';
import { useQuery, UseQueryOptions } from '@tanstack/react-query';

import { ENDPOINTS } from '@/api/endpoints';
import { httpClient } from '@/api/http-client';
import {
  CwfActiveDocumentByCarePlanRequest,
  CwfActiveDocumentByCarePlanResponse,
  CwfActiveDocumentByType,
  CwfActiveDocumentByTypeRequest,
  CwfActiveDocumentsRequest,
  CwfActiveDocumentsResponse,
  CwfDocumentTypesResponse,
} from '@/api/model';

const KEY = {
  GET_ACTIVE_DOCUMENTS: 'GET_ACTIVE_DOCUMENTS',
  GET_DOCUMENT_BY_CARE_PLAN: 'GET_DOCUMENT_BY_CARE_PLAN',
  GET_DOCUMENT_BY_TYPE: 'GET_DOCUMENT_BY_TYPE',
  GET_DOCUMENT_TYPES: 'GET_DOCUMENT_TYPES',
} as const;

const request = {
  getActiveDocumentByCarePlan: async (
    {
      payload: {
        params: { care_activity_id: careActivityId, care_plan_id: carePlanId, type_id: typeId },
      },
    }: CwfActiveDocumentByCarePlanRequest,
    signal?: AbortSignal,
  ) => {
    const { data } = await httpClient.get<CwfActiveDocumentByCarePlanResponse>(
      `${ENDPOINTS.CWF.DOCUMENTS.CARE_PLANS}/${carePlanId}/activities/${careActivityId}`,
      {
        params: { type_id: typeId },
        signal,
      },
    );

    return data;
  },
  getActiveDocumentByType: async (
    {
      payload: {
        params: { study_id: studyId, type_id: typeId },
      },
    }: CwfActiveDocumentByTypeRequest,
    signal?: AbortSignal,
  ) => {
    const { data } = await httpClient.get<CwfActiveDocumentByType>(
      `${ENDPOINTS.CWF.DOCUMENTS.STUDY}/${studyId}/type/${typeId}`,
      {
        signal,
      },
    );

    return data;
  },
  getActiveDocuments: async (
    { payload: { params } }: CwfActiveDocumentsRequest,
    signal?: AbortSignal,
  ) => {
    const { data } = await httpClient.get<CwfActiveDocumentsResponse>(
      ENDPOINTS.CWF.DOCUMENTS.ACTIVE,
      { params: removeEmptyParams(params), signal },
    );

    return data;
  },
  getDocumentTypes: async (signal?: AbortSignal) => {
    const { data } = await httpClient.get<CwfDocumentTypesResponse>(ENDPOINTS.CWF.DOCUMENT_TYPES, {
      signal,
    });

    return data;
  },
};

const query = {
  useGetActiveDocumentByCarePlan: (
    { payload }: CwfActiveDocumentByCarePlanRequest,
    config?: Partial<UseQueryOptions<CwfActiveDocumentByCarePlanResponse, PulseError>>,
  ) =>
    useQuery<CwfActiveDocumentByCarePlanResponse, PulseError>({
      queryFn: ({ signal }) => request.getActiveDocumentByCarePlan({ payload }, signal),
      queryKey: [
        KEY.GET_DOCUMENT_BY_CARE_PLAN,
        payload.params.care_plan_id,
        payload.params.care_activity_id,
        payload.params.type_id,
      ],
      ...config,
    }),
  useGetActiveDocumentByType: (
    { payload }: CwfActiveDocumentByTypeRequest,
    config?: Partial<UseQueryOptions<CwfActiveDocumentByType, PulseError>>,
  ) =>
    useQuery<CwfActiveDocumentByType, PulseError>({
      queryFn: ({ signal }) => request.getActiveDocumentByType({ payload }, signal),
      queryKey: [KEY.GET_DOCUMENT_BY_TYPE, payload.params.study_id, payload.params.type_id],
      ...config,
    }),
  useGetActiveDocuments: (
    { payload }: CwfActiveDocumentsRequest,
    config?: Partial<UseQueryOptions<CwfActiveDocumentsResponse, PulseError>>,
  ) =>
    useQuery<CwfActiveDocumentsResponse, PulseError>({
      enabled: !!payload.params.patient_id,
      queryFn: ({ signal }) => request.getActiveDocuments({ payload }, signal),
      queryKey: [KEY.GET_ACTIVE_DOCUMENTS, payload.params],
      ...config,
    }),
  useGetDocumentTypes: (config?: Partial<UseQueryOptions<CwfDocumentTypesResponse, PulseError>>) =>
    useQuery<CwfDocumentTypesResponse, PulseError>({
      queryFn: ({ signal }) => request.getDocumentTypes(signal),
      queryKey: [KEY.GET_DOCUMENT_TYPES],
      ...config,
    }),
};

export const cwf = {
  KEY,
  query,
  request,
};
